import {Stomp, CompatClient, IMessage} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type CallbackHandler = (err: any, res?: Response) => void;

export interface ResponseWithBody<S extends number, T> {
  status: S;
  body: T;
}

export type AccountingExportPdfCreatedNotification = {
  accountingExportId: string;
  handle: string;
  pushNotificationType: 'AccountingExportPdfCreatedNotification';
};

export type ActivityEntity = {
  day: string;
  id: string;
  type: 'ActivityEntity';
};

export type ActivitySharedNotification = {
  context: 'TIMETRACKING';
  entity: ActivityEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ActivitySharedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type CasusEntity = {
  id: string;
  type: 'CasusEntity';
};

export type CasusSpecificPushNotifications = {
  pushNotificationType:
    | 'DocumentPdfCreatedPushNotification'
    | 'DocumentPdfCreationFailedPushNotification'
    | 'DocumentPdfSignedPushNotification'
    | 'DocumentPdfSigningFailedPushNotification'
    | 'DocumentPreviewPdfCreatedPushNotification'
    | 'ZipExtractedPushNotification'
    | 'ZipExtractionFailedPushNotification';
};

export type CommunicationPushNotification = {
  needsAttention: boolean;
  pushNotificationType: 'CommunicationPushNotification';
};

export type CommunicationSummaryResponse = {
  needsAttention: boolean;
};

export type CommunicationUserSpecificPushNotifications = {
  pushNotificationType:
    | 'CommunicationPushNotification'
    | 'EmailAccountAuthenticationFailedPushNotification'
    | 'NewEmailReceivedPushNotification';
};

export type DocumentEntity = {
  casusId: string;
  documentVersionId: string;
  folderId?: string;
  id: string;
  type: 'DocumentEntity';
};

export type DocumentManagementUserSpecificPushNotifications = {
  pushNotificationType:
    | 'TemporaryDocumentCreatedPushNotification'
    | 'TemporaryDocumentPdfCreatedPushNotification'
    | 'TemporaryDocumentPdfCreationFailedPushNotification'
    | 'UserAddedNewDocumentVersionPushNotification';
};

export type DocumentPdfCreatedPushNotification = {
  casusId: string;
  documentId: string;
  pushNotificationType: 'DocumentPdfCreatedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfCreationFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'DocumentPdfCreationFailedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfSignedPushNotification = {
  casusId: string;
  documentId: string;
  pushNotificationType: 'DocumentPdfSignedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPdfSigningFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'DocumentPdfSigningFailedPushNotification';
  sourceDocumentId: string;
};

export type DocumentPreviewPdfCreatedPushNotification = {
  casusId: string;
  documentId: string;
  documentVersionId: string;
  pushNotificationType: 'DocumentPreviewPdfCreatedPushNotification';
};

export type DocumentSharedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: DocumentEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'DocumentSharedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type DocumentUploadFinishedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'DocumentUploadFinishedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type DownloadExpiredCondition = {
  conditionType: 'DownloadExpiredCondition';
};

export type EmailAccountAuthenticationFailedPushNotification = {
  emailAccountId: string;
  name: string;
  pushNotificationType: 'EmailAccountAuthenticationFailedPushNotification';
  userName: string;
};

export type Entity = {
  id: string;
  type: string;
};

export type FeeDeletedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'FeeDeletedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type FeesShouldBeCompletedNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'FeesShouldBeCompletedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type FinanceExportUserSpecificPushNotifications = {
  pushNotificationType: 'AccountingExportPdfCreatedNotification';
};

export type InaccessibleActivityCreatedNotification = {
  context: 'TIMETRACKING';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InaccessibleActivityCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InformativeObligationVersionDeadlineReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InformativeObligationVersionDeadlineReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerCompleteByDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: InvoiceContainerEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceContainerCompleteByDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity: InvoiceContainerEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceContainerDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type InvoiceContainerEntity = {
  id: string;
  type: 'InvoiceContainerEntity';
};

export type InvoiceDocumentsCreatedNotification = {
  invoiceContainerId: string;
  pushNotificationType: 'InvoiceDocumentsCreatedNotification';
};

export type InvoiceManagementUserSpecificPushNotifications = {
  pushNotificationType:
    | 'InvoiceDocumentsCreatedNotification'
    | 'InvoicePreviewPdfCreatedNotification'
    | 'ReceiptPdfExportReadyNotification'
    | 'TransactionSetPdfExportReadyNotification';
};

export type InvoicePreviewPdfCreatedNotification = {
  invoiceContainerId: string;
  pushNotificationType: 'InvoicePreviewPdfCreatedNotification';
};

export type InvoiceReminderDueDateReachedNotification = {
  context: 'INVOICE_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'InvoiceReminderDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type JustitiaSubmissionReceivedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: SubmissionEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'JustitiaSubmissionReceivedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type NewEmailReceivedPushNotification = {
  emailId: string;
  pushNotificationType: 'NewEmailReceivedPushNotification';
};

export type Notification = {
  context:
    | 'CASUS_MANAGEMENT'
    | 'DOCUMENT_MANAGEMENT'
    | 'TASK_MANAGEMENT'
    | 'INVOICE_MANAGEMENT'
    | 'OBLIGATION_MANAGEMENT'
    | 'TIMETRACKING';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name:
    | 'TaskAssignedNotification'
    | 'ActivitySharedNotification'
    | 'SetAsResponsiblePersonOfTheCasusNotification'
    | 'RemovedAsResponsiblePersonOfTheCasusNotification'
    | 'DocumentSharedNotification'
    | 'ZipFileCreatedNotification'
    | 'ZipFileCreationFailedNotification'
    | 'TaskDueDateReachedNotification'
    | 'DocumentUploadFinishedNotification'
    | 'FeesShouldBeCompletedNotification'
    | 'InvoiceContainerCompleteByDueDateReachedNotification'
    | 'InvoiceContainerDueDateReachedNotification'
    | 'InvoiceReminderDueDateReachedNotification'
    | 'ObligationVersionReviewNeededNotification'
    | 'ObligationVersionCreatedNotification'
    | 'ObligationVersionApprovedNotification'
    | 'ObligationVersionRejectedNotification'
    | 'ObligationReminderTimestampReachedNotification'
    | 'InformativeObligationVersionDeadlineReachedNotification'
    | 'ResolvableObligationVersionDeadlineReachedNotification'
    | 'InaccessibleActivityCreatedNotification'
    | 'FeeDeletedNotification'
    | 'JustitiaSubmissionReceivedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE' | 'RESOLVABLE';
};

export type NotificationCenterPushNotification = {
  addedNotifications: boolean;
  notificationSummary: NotificationSummary;
  pushNotificationType: 'NotificationCenterPushNotification';
};

export type NotificationSettings = {
  grouped: boolean;
  showOnlyImportant: boolean;
  visibleNotifications: 'ALL' | 'INFORMATIVE' | 'RESOLVABLE';
};

export type NotificationSummary = {
  hasImportantNotification: boolean;
  unreadInformativeNotifications: number;
  unreadResolvableNotifications: number;
};

export type NotificationSummaryResponse = {
  notificationSummary: NotificationSummary;
};

export type NotificationUpdateParams = {
  notifyAt: string;
};

export type NotificationUserSpecificPushNotifications = {
  pushNotificationType: 'NotificationCenterPushNotification';
};

export type NotificationsResponse = {
  notifications: Array<Notification>;
  settings: NotificationSettings;
  summary: NotificationSummary;
};

export type ObligationEntity = {
  id: string;
  obligationVersionId: string;
  type: 'ObligationEntity';
};

export type ObligationReminderEntity = {
  id: string;
  obligationId: string;
  type: 'ObligationReminderEntity';
};

export type ObligationReminderTimestampReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationReminderEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationReminderTimestampReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationResponse = {
  deadline: string;
  id: string;
  note: string;
  responsibleUser: UserInResponse;
};

export type ObligationVersionApprovedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionApprovedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionCreatedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionRejectedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionRejectedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ObligationVersionReviewNeededNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ObligationVersionReviewNeededNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ReceiptPdfExportReadyNotification = {
  downloadUrl: string;
  handle: string;
  id: string;
  pushNotificationType: 'ReceiptPdfExportReadyNotification';
};

export type RemovedAsResponsiblePersonOfTheCasusNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'RemovedAsResponsiblePersonOfTheCasusNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ResolvableObligationVersionDeadlineReachedNotification = {
  context: 'OBLIGATION_MANAGEMENT';
  entity: ObligationEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ResolvableObligationVersionDeadlineReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'RESOLVABLE';
};

export type ResolvableObligationVersionDeadlineReachedResolutionParams = {
  obligationState: 'OBSOLETE' | 'FULFILLED' | 'MISSED';
  resolution: 'CHANGE_STATE';
  type: 'ResolvableObligationVersionDeadlineReachedNotification';
};

export type SetAsResponsiblePersonOfTheCasusNotification = {
  context: 'CASUS_MANAGEMENT';
  entity: CasusEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'SetAsResponsiblePersonOfTheCasusNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type SubmissionEntity = {
  casusId: string;
  id: string;
  type: 'SubmissionEntity';
};

export type TaskAssignedNotification = {
  context: 'TASK_MANAGEMENT';
  entity: TaskEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'TaskAssignedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type TaskDueDateReachedNotification = {
  context: 'TASK_MANAGEMENT';
  entity: TaskEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'TaskDueDateReachedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'RESOLVABLE';
};

export type TaskDueDateReachedResolutionParams = {
  rescheduleDate?: string;
  resolution: 'RESCHEDULE' | 'COMPLETE';
  type: 'TaskDueDateReachedNotification';
};

export type TaskEntity = {
  casusId: string;
  id: string;
  type: 'TaskEntity';
};

export type TaskResponse = {
  assignedUser?: UserInResponse;
  criticality: 'LOW' | 'MEDIUM' | 'HIGH';
  description: string;
  dueDate?: string;
  id: string;
};

export type TemporaryDocumentCreatedPushNotification = {
  pushNotificationType: 'TemporaryDocumentCreatedPushNotification';
  temporaryDocumentId: string;
  type:
    | 'EMAIL_DOCUMENT'
    | 'EXCEL_DOCUMENT'
    | 'POWERPOINT_DOCUMENT'
    | 'PDF_DOCUMENT'
    | 'WORD_DOCUMENT'
    | 'IMAGE_DOCUMENT'
    | 'TEXT_DOCUMENT'
    | 'RTF_DOCUMENT'
    | 'ZIP_DOCUMENT'
    | 'UNKNOWN_DOCUMENT';
};

export type TemporaryDocumentPdfCreatedPushNotification = {
  pushNotificationType: 'TemporaryDocumentPdfCreatedPushNotification';
  temporaryDocumentId: string;
};

export type TemporaryDocumentPdfCreationFailedPushNotification = {
  pushNotificationType: 'TemporaryDocumentPdfCreationFailedPushNotification';
  temporaryDocumentId: string;
};

export type TransactionSetPdfExportReadyNotification = {
  handle: string;
  pushNotificationType: 'TransactionSetPdfExportReadyNotification';
  transactionSetId: string;
};

export type Unit = {};

export type UserAddedNewDocumentVersionPushNotification = {
  casusId: string;
  documentId: string;
  folderId: string;
  lastModifiedAt: string;
  pushNotificationType: 'UserAddedNewDocumentVersionPushNotification';
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type ZipExtractedPushNotification = {
  casusId: string;
  pushNotificationType: 'ZipExtractedPushNotification';
  zipFileId: string;
};

export type ZipExtractionFailedPushNotification = {
  casusId: string;
  pushNotificationType: 'ZipExtractionFailedPushNotification';
  zipFileId: string;
};

export type ZipFileCreatedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity: ZipFileEntity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ZipFileCreatedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ZipFileCreationFailedNotification = {
  context: 'DOCUMENT_MANAGEMENT';
  entity?: Entity;
  id: string;
  importantFlag: boolean;
  message: string;
  name: 'ZipFileCreationFailedNotification';
  notifyAt: string;
  readFlag: boolean;
  type: 'INFORMATIVE';
};

export type ZipFileEntity = {
  id: string;
  type: 'ZipFileEntity';
};

export type Response_downloadZipFile_200 = {};

export type Response_getEmailIndication_200 = boolean;

export type Logger = {log: (line: string) => any};

/*
 *
 * @class AsyncBackend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class AsyncBackend {
  private domain: string = '';
  private errorHandlers: CallbackHandler[] = [];
  private stompClient: CompatClient;

  private connected = false;

  private eventTarget = new EventTarget();

  public constructor(domain?: string) {
    if (domain) {
      this.domain = domain;
    }

    this.stompClient = Stomp.over(() => new SockJS('/websocket'));
    this.stompClient.debug = (): void => {};
    this.stompClient.connect(
      {},
      () => {
        this.connected = true;
        this.eventTarget.dispatchEvent(new Event('connect'));
      },
      () => {
        this.connected = false;
        this.eventTarget.dispatchEvent(new Event('disconnect'));
      }
    );
    this.stompClient.reconnect_delay = 5_000;

    this.topicCasusSpecific = this.topicCasusSpecific.bind(this);
    this.topicCommunicationUserSpecific =
      this.topicCommunicationUserSpecific.bind(this);
    this.topicDocumentManagementUserSpecific =
      this.topicDocumentManagementUserSpecific.bind(this);
    this.topicFinanceExportUserSpecific =
      this.topicFinanceExportUserSpecific.bind(this);
    this.topicInvoiceManagementUserSpecific =
      this.topicInvoiceManagementUserSpecific.bind(this);
    this.topicNotificationUserSpecific =
      this.topicNotificationUserSpecific.bind(this);
  }

  public initializeOnConnect(callback: () => void): void {
    if (this.connected) {
      callback();
    }
    this.eventTarget.addEventListener('connect', callback);
  }

  public isConnected(): boolean {
    return this.connected;
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  private static parseMessage<T>(message: IMessage): ResponseWithBody<200, T> {
    return {body: JSON.parse(message.body), status: 200};
  }

  private subscribe<T>(
    topic: string,
    handler: (notification: ResponseWithBody<200, T>) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error(
        `Trying to subscribe to topic "${topic}" while disconnected!`
      );
    }

    const {unsubscribe} = this.stompClient.subscribe(
      topic,
      (message: IMessage) => {
        handler(AsyncBackend.parseMessage<T>(message));
      }
    );

    return {unsubscribe};
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  public static topicCasusSpecificURL(parameters: {casusId: string}): string {
    let path = '/topic/casus-{casusId}';

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicCasusSpecific
   * @param {string} casusId -
   */
  public topicCasusSpecific(
    parameters: {
      casusId: string;
    },
    handler: (
      notification: ResponseWithBody<200, CasusSpecificPushNotifications>
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicCasusSpecificURL(parameters),
      handler
    );
  }
  public static topicCommunicationUserSpecificURL(parameters: {
    userId: string;
  }): string {
    let path = '/topic/communication_user-{userId}';

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicCommunicationUserSpecific
   * @param {string} userId -
   */
  public topicCommunicationUserSpecific(
    parameters: {
      userId: string;
    },
    handler: (
      notification: ResponseWithBody<
        200,
        CommunicationUserSpecificPushNotifications
      >
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicCommunicationUserSpecificURL(parameters),
      handler
    );
  }
  public static topicDocumentManagementUserSpecificURL(parameters: {
    userId: string;
  }): string {
    let path = '/topic/document-management_user-{userId}';

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicDocumentManagementUserSpecific
   * @param {string} userId -
   */
  public topicDocumentManagementUserSpecific(
    parameters: {
      userId: string;
    },
    handler: (
      notification: ResponseWithBody<
        200,
        DocumentManagementUserSpecificPushNotifications
      >
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicDocumentManagementUserSpecificURL(parameters),
      handler
    );
  }
  public static topicFinanceExportUserSpecificURL(parameters: {
    userId: string;
  }): string {
    let path = '/topic/finance-export-user-{userId}';

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicFinanceExportUserSpecific
   * @param {string} userId -
   */
  public topicFinanceExportUserSpecific(
    parameters: {
      userId: string;
    },
    handler: (
      notification: ResponseWithBody<
        200,
        FinanceExportUserSpecificPushNotifications
      >
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicFinanceExportUserSpecificURL(parameters),
      handler
    );
  }
  public static topicInvoiceManagementUserSpecificURL(parameters: {
    userId: string;
  }): string {
    let path = '/topic/invoice-management_user-{userId}';

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicInvoiceManagementUserSpecific
   * @param {string} userId -
   */
  public topicInvoiceManagementUserSpecific(
    parameters: {
      userId: string;
    },
    handler: (
      notification: ResponseWithBody<
        200,
        InvoiceManagementUserSpecificPushNotifications
      >
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicInvoiceManagementUserSpecificURL(parameters),
      handler
    );
  }
  public static topicNotificationUserSpecificURL(parameters: {
    userId: string;
  }): string {
    let path = '/topic/notification_user-{userId}';

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        AsyncBackend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    return path;
  }

  /*
   *
   * @method
   * @name AsyncBackend#topicNotificationUserSpecific
   * @param {string} userId -
   */
  public topicNotificationUserSpecific(
    parameters: {
      userId: string;
    },
    handler: (
      notification: ResponseWithBody<
        200,
        NotificationUserSpecificPushNotifications
      >
    ) => void
  ): {unsubscribe: () => void} {
    if (!this.connected) {
      throw new Error('Websocket not connected');
    }
    return this.subscribe(
      AsyncBackend.topicNotificationUserSpecificURL(parameters),
      handler
    );
  }
}

export default new AsyncBackend(process.env.REACT_APP_SERVER_URL || '/api');
